// hh-be3 (case-35) — DestructCascadeScreen.
//
// Connected wrapper reached from EditPropertyScreen when the user asks to
// delete a property. On mount it calls POST /api/v1/destruct/preview
// { propertyId } to resolve the full cascade, then offers a choice (case-35):
//
//   • «Перенести содержимое» — move rooms / items / equipment into another
//     property and delete the now-empty source (MoveContentsScreen → hh-mvm).
//   • «Удалить со всем содержимым» — the destructive path: DestructPreviewScreen
//     shows the explicit list, then POST /api/v1/destruct/confirm. A 410
//     (expired/used token) is surfaced so the user can go back and retry.

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { T, Icon, Btn, type IconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { destruct, humanizeError } from '../../api';
import type { DestructPreviewResponse } from '../../api/endpoints/destruct.js';
import { DestructPreviewScreen } from './DestructPreviewScreen';
import { MoveContentsScreen } from './MoveContentsScreen';

export interface DestructCascadeScreenProps {
  readonly router: RouterApi;
  readonly propertyId: string;
}

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  // Каскад разрешён — пользователь выбирает: перенести или удалить (case-35).
  | { readonly kind: 'choose'; readonly preview: DestructPreviewResponse }
  // Ветка удаления: подтверждение в DestructPreviewScreen.
  | { readonly kind: 'destruct'; readonly preview: DestructPreviewResponse }
  | { readonly kind: 'confirming'; readonly preview: DestructPreviewResponse }
  | { readonly kind: 'confirm-error'; readonly message: string }
  // Ветка переноса содержимого (hh-mvm).
  | { readonly kind: 'move'; readonly preview: DestructPreviewResponse };

export function DestructCascadeScreen({
  router,
  propertyId,
}: DestructCascadeScreenProps): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });

  // Resolve the cascade preview once on mount.
  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    destruct
      .previewDestruct({ propertyId })
      .then((preview) => {
        if (cancelled) return;
        setState({ kind: 'choose', preview });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', message: humanizeError(err, 'load') });
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const back = (): void => router.back();

  const confirm = (confirmToken: string): void => {
    setState((prev) =>
      prev.kind === 'destruct' ? { kind: 'confirming', preview: prev.preview } : prev,
    );
    destruct
      .confirmDestruct({ confirmToken })
      .then(() => {
        router.go('tree');
      })
      .catch((err: unknown) => {
        // Most likely a 410 (token expired / already used) — show it and let
        // the user go back to start a fresh preview.
        setState({ kind: 'confirm-error', message: humanizeError(err, 'delete') });
      });
  };

  if (state.kind === 'loading' || state.kind === 'confirming') {
    return (
      <div style={containerStyle}>
        <ScreenHeader
          title="Удаление собственности"
          subtitle={state.kind === 'confirming' ? 'Удаляю…' : 'Считаю, что внутри…'}
          onBack={back}
        />
        <div style={noteStyle}>
          {state.kind === 'confirming' ? 'Удаляю…' : 'Загружаю предпросмотр…'}
        </div>
      </div>
    );
  }

  if (state.kind === 'error' || state.kind === 'confirm-error') {
    return (
      <div style={containerStyle}>
        <ScreenHeader
          title="Не получилось"
          subtitle="Можно вернуться и попробовать заново"
          onBack={back}
        />
        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          <div style={{ fontSize: 14, color: T.danger, fontWeight: 500, marginBottom: 16 }}>
            {state.message}
          </div>
          <Btn kind="ghost" onClick={back} style={{ width: '100%' }}>
            Назад
          </Btn>
        </div>
      </div>
    );
  }

  if (state.kind === 'choose') {
    return (
      <ChooseActionScreen
        preview={state.preview}
        onBack={back}
        onMove={() => setState({ kind: 'move', preview: state.preview })}
        onDelete={() => setState({ kind: 'destruct', preview: state.preview })}
      />
    );
  }

  if (state.kind === 'move') {
    return (
      <MoveContentsScreen
        router={router}
        sourcePropertyId={propertyId}
        sourceName={sourceName(state.preview)}
        counts={state.preview.counts}
        onBack={() => setState({ kind: 'choose', preview: state.preview })}
      />
    );
  }

  // state.kind === 'destruct'
  return (
    <DestructPreviewScreen
      preview={state.preview}
      mode="cascade"
      onCancel={() => setState({ kind: 'choose', preview: state.preview })}
      onConfirm={confirm}
    />
  );
}

/** Имя удаляемой собственности из preview (для подзаголовков). */
function sourceName(preview: DestructPreviewResponse): string {
  return preview.affected.properties[0]?.name ?? 'собственность';
}

// ─────────────────────────────────────────────────────────
// Шаг выбора (case-35): перенести содержимое или удалить всё.
// ─────────────────────────────────────────────────────────

interface ChooseActionScreenProps {
  readonly preview: DestructPreviewResponse;
  readonly onBack: () => void;
  readonly onMove: () => void;
  readonly onDelete: () => void;
}

function ChooseActionScreen({
  preview,
  onBack,
  onMove,
  onDelete,
}: ChooseActionScreenProps): JSX.Element {
  const { counts } = preview;
  const inside = counts.rooms + counts.items + counts.equipment;
  const name = sourceName(preview);

  return (
    <div style={containerStyle}>
      <ScreenHeader
        title={`Что сделать с «${name}»?`}
        subtitle={
          inside === 0
            ? 'Внутри пусто'
            : `Внутри: ${counts.rooms} помещ. · ${counts.items} вещей · ${counts.equipment} оборуд.`
        }
        onBack={onBack}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px 16px' }}>
        {/* Безопасный вариант первым. */}
        <ChoiceCard
          icon="folder"
          tone="brand"
          title="Перенести содержимое"
          body="Помещения, вещи и оборудование переедут в другую собственность. Эта — удалится, но ничего не потеряется."
          onClick={onMove}
        />
        <ChoiceCard
          icon="trash"
          tone="danger"
          title="Удалить со всем содержимым"
          body="Собственность и всё внутри будут удалены. Откат доступен ещё сутки после удаления."
          onClick={onDelete}
        />
      </div>
    </div>
  );
}

interface ChoiceCardProps {
  readonly icon: IconName;
  readonly tone: 'brand' | 'danger';
  readonly title: string;
  readonly body: string;
  readonly onClick: () => void;
}

function ChoiceCard({ icon, tone, title, body, onClick }: ChoiceCardProps): JSX.Element {
  const accent = tone === 'danger' ? T.danger : T.brandDeep;
  const bg = tone === 'danger' ? T.dangerSoft : T.brandSoft;
  const bd = tone === 'danger' ? '#E9C7BF' : T.brandSoft2;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        marginTop: 12,
        padding: 16,
        background: bg,
        border: `1px solid ${bd}`,
        borderRadius: 16,
        fontFamily: T.font,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        textAlign: 'left',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: T.surface,
          border: `1px solid ${bd}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={20} color={accent} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: accent }}>{title}</div>
        <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.4 }}>{body}</div>
      </div>
      <Icon name="chevR" size={16} color={T.ink3} />
    </button>
  );
}

const containerStyle: JSX.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  background: T.bg,
};

const noteStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  fontSize: 14,
  fontWeight: 500,
  color: T.ink3,
};
